/**
 * Eras (1.K-1 deliverable 4). An era's range starts at the start of its `start` date (or reaches
 * back without limit) and ends, exclusive, at the start of its `end` date, else at the next era to
 * start, else never; `compile` computes both ends. Era years change at the calendar's new year:
 * counting up, era year = anchor.eraYear + (Y − anchor.year); counting down, anchor.eraYear −
 * (Y − anchor.year), for every absolute year Y, inside the era's range or not.
 */
import { isTick } from '@codex/contracts';
import { add, floorDiv, sub } from './math.js';
import { isCompiled } from './types.js';
import { yearOfDays } from './year.js';
/** The era year of absolute year `year` in `era`, or NaN when it is not a safe integer. */
export function eraYearOf(era, year) {
    const offset = sub(year, era.anchorYear);
    return era.direction === 'up' ? add(era.anchorEraYear, offset) : sub(era.anchorEraYear, offset);
}
/** The absolute year of era year `eraYear` in `era`, or NaN when it is not a safe integer. */
export function absoluteYearOf(era, eraYear) {
    const offset = sub(eraYear, era.anchorEraYear);
    return era.direction === 'up' ? add(era.anchorYear, offset) : sub(era.anchorYear, offset);
}
export function eraContains(era, tick) {
    return (era.start === null || tick >= era.start) && (era.end === null || tick < era.end);
}
/** The first era in definition order whose range contains `tick` (Base §6), or undefined. */
export function eraAt(calendar, tick) {
    return calendar.eras.find((era) => eraContains(era, tick));
}
export function eraByKey(calendar, key) {
    return calendar.eras.find((era) => era.key === key);
}
/**
 * The era whose range contains `tick`, with the era year of the tick's absolute year; where
 * ranges overlap, the earlier era in definition order wins. Null when no era contains the tick,
 * without a compiled calendar, for a value that is not a tick, or when the era year is not a safe
 * integer.
 */
export function eraOf(calendar, tick) {
    if (!isCompiled(calendar) || !isTick(tick))
        return null;
    const era = eraAt(calendar, tick);
    if (era === undefined)
        return null;
    const located = yearOfDays(calendar, floorDiv(tick, calendar.secondsPerDay));
    if (located === null)
        return null;
    const year = eraYearOf(era, located.year);
    return Number.isSafeInteger(year) ? { key: era.key, year } : null;
}
/**
 * The key of the era with the latest start (an era with no start counts as the earliest); among
 * equals, the first in definition order. Parts that omit `era` are read in it.
 */
export function defaultEra(calendar) {
    return isCompiled(calendar) ? calendar.defaultEra.key : null;
}
//# sourceMappingURL=eras.js.map